import { useEffect, useState } from 'react';
import { Button } from '@primereact/ui/button';
import { InputText } from '@primereact/ui/inputtext';

import { EsDialog } from './EsPrime';
import { useEsStrings } from './EsStrings';

/**
 * The gate in front of a delete: one dialog, one shape, every screen.
 *
 * <p><b>Why it exists.</b> Six deletes in this application removed a row on a
 * single click -- the four codex admin lists, the talk list, and taking a
 * combatant out of the fight. Three others had a confirmation of their own and
 * each had written its own: EsAccountList, EsCharacterSheet's EsDeleteDialog and
 * EsCampaign's remove-a-seat dialog are three implementations of one idea.
 * Rather than a fourth copy, this is the shape, and the screens that had none
 * take it.
 *
 * <p><b>A gate is not a speed bump.</b> What makes one worth pressing through
 * is that it says what is about to be lost -- the row's own name, and where it
 * goes -- so `what` is a required sentence rather than an optional flourish. A
 * dialog reading "Are you sure?" teaches a reader to press the button without
 * looking, which is worse than no dialog at all.
 *
 * <p><b>`word` raises the gate to a typed one</b>, which is a different kind of
 * check rather than a louder one. A yes/no box is one mis-aimed click away from
 * the thing it guards; typing the row's own name cannot be satisfied by a
 * reflex, and it is itself a moment of reading WHICH row is about to go. The
 * pattern is EsCharacterSheet's, which asked for it first and for the same
 * reason -- no undo, no trash, no soft delete a screen can reverse -- and it is
 * lifted here rather than copied a second time. Left undefined, the dialog is
 * the ordinary two-button one, which is right for anything a join code or a
 * re-add can bring back.
 *
 * <p>Case and surrounding space are forgiven; the word is not. The confirm is
 * DISABLED until it matches rather than refusing on click, because a disabled
 * button says what is missing before it is pressed. A caller whose row may have
 * no name passes a constant -- 'DELETE' -- so the check can never be passed by
 * typing nothing into an empty box.
 *
 * <p><b>Hooks are derived from `data-testid`</b>, the house convention: the
 * dialog itself, `<id>-title`, `<id>-message`, `<id>-confirm` and `<id>-cancel`,
 * plus `<id>-prompt` and `<id>-input` when there is a word to type. A feature
 * file names the one id and reaches all of them, which is what EsPrime's own
 * wrappers do.
 *
 * <p>`busy` disables both buttons while the DELETE is in flight, so a double
 * press cannot send two. The caller still keeps the pending row -- which row is
 * pending is the list's business and the dialog is one element however many rows
 * there are. The ONE piece of state held here is the half-typed word, and it
 * belongs here because it is about this dialog rather than about the list: it is
 * cleared whenever the dialog closes, so a cancelled delete never leaves an
 * armed box waiting for the next one.
 *
 * @param open      whether the dialog is up
 * @param title     the question, as a question
 * @param what      what is about to be lost, in a sentence
 * @param word      typed to arm the confirm; omitted, there is nothing to type
 * @param prompt    the sentence over the box, in place of "Type {word} to confirm" --
 *                  for a word the prompt should not print, such as an address
 * @param onTyped   told what is in the box as it changes, for a caller whose request
 *                  carries the typed word rather than its own copy of it
 * @param confirm   the confirm button's word, default Delete
 * @param severity  the confirm button's colour, default danger; null for the
 *                  app's ordinary primary, which a default value cannot express
 * @param busy      whether the delete is in flight
 * @param onConfirm pressed the confirm button
 * @param onCancel  cancelled, dismissed, or pressed Escape
 * @param children  a choice the delete needs, under the sentence -- where a
 *                  removed rulesys's packs and campaigns go
 */
export default function EsConfirmDelete( {
    open, title, what, word, prompt, confirm, severity = 'danger', busy = false,
    onConfirm, onCancel, onTyped, children, 'data-testid': dataTestId,
} ) {
    const t = useEsStrings();
    const [ typed, setTyped ] = useState( '' );

    const aOpen = Boolean( open );

    // Cleared on the way IN as well as on the way out. Clearing in the two
    // handlers alone misses every other way a dialog closes -- Escape, the X,
    // a click on the backdrop, and a caller that simply stops passing open --
    // and the sheet's version had to clear in close() for exactly that reason.
    // Keyed on the transition rather than on every render so typing survives.
    useEffect( () => { if ( !aOpen ) setTyped( '' ); }, [ aOpen ] );

    const aArmed = !word || typed.trim().toLowerCase() === word.trim().toLowerCase();

    return (
        <EsDialog open={ aOpen }
                  data-testid={ dataTestId }
                  onOpenChange={ ( pOpen ) => { if ( !pOpen && onCancel ) onCancel(); } }
                  title={ title }
                  footer={
                      <div className="campaignFormButtons">
                          { /* The confirm carries the danger colour and the
                               cancel is the quiet one, so the button that
                               destroys something never looks like the default
                               press. severity is a prop rather than a constant
                               because not every press this dialog gates is
                               destructive -- leaving a table is reversible by
                               the same join code, and red on it would be the
                               screen lying about what happens next.

                               null rather than undefined for "no severity, the
                               ordinary primary button": a default parameter
                               fires on undefined, so undefined is already spoken
                               for by the danger default and cannot also mean the
                               opposite. Button reads either as absent. */ }
                          <Button data-testid={ dataTestId && `${ dataTestId }-confirm` }
                                  severity={ severity }
                                  disabled={ busy || !aArmed } onClick={ onConfirm }>
                              { confirm ?? t( 'common-confirm-delete', 'Delete' ) }
                          </Button>
                          <Button data-testid={ dataTestId && `${ dataTestId }-cancel` }
                                  severity="secondary" variant="outlined"
                                  disabled={ busy } onClick={ onCancel }>
                              { t( 'common-cancel', 'Cancel' ) }
                          </Button>
                      </div> }>
            <p data-testid={ dataTestId && `${ dataTestId }-message` }>{ what }</p>

            { children }

            { word &&
                // Tailwind utilities inline rather than a class of its own:
                // this component is imported from everywhere and owns no
                // stylesheet, so a class here would have to live in somebody
                // else's file and load only when that screen did.
                <div className="mt-4">
                    { /* The label is a real <label> pointing at the field, and
                         the field's id is derived from the dialog's so two
                         dialogs mounted at once cannot both answer to it. */ }
                    <label className="mb-1 block text-left text-sm text-body"
                           htmlFor={ `${ dataTestId || 'esConfirm' }-input` }
                           data-testid={ dataTestId && `${ dataTestId }-prompt` }>
                        { prompt ?? t( 'common-confirm-prompt', 'Type {word} to confirm', { word: <b>{ word }</b> } ) }
                    </label>
                    <InputText id={ `${ dataTestId || 'esConfirm' }-input` }
                               fluid autoComplete="off"
                               data-testid={ dataTestId && `${ dataTestId }-input` }
                               disabled={ busy }
                               value={ typed }
                               onChange={ ( pEvent ) => {
                                   setTyped( pEvent.target.value );
                                   if ( onTyped ) onTyped( pEvent.target.value );
                               } } />
                </div> }
        </EsDialog>
    );
}
